/**
 * FeatureContext — single fetch of /api/tenants/current/plan, exposed via
 * useFeatureGate(key). Backend remains authoritative; this only powers UI
 * visibility (hide buttons / panels for plans that don't include them).
 *
 * Failure mode: if the endpoint errors, we fall back to "everything enabled"
 * so we never lock a paying customer out of their UI on a transient 5xx.
 * Backend will still 403 anything they shouldn't see.
 */
import React, { createContext, useContext, useEffect, useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FeatureContext = createContext({
  ready: false,
  planId: null,
  planLabel: null,
  features: {},   // { key: 'limited' | true | false }
  isEnabled: () => true,
  isLimited: () => false,
  refresh: async () => {},
});

export function FeatureProvider({ children }) {
  const [state, setState] = useState({
    ready: false, planId: null, planLabel: null, features: {},
  });

  const load = async () => {
    try {
      const res = await fetch(`${API_URL}/api/tenants/current/plan`);
      if (!res.ok) {
        // Soft fail-open. Backend stays authoritative on 403s.
        setState({ ready: true, planId: null, planLabel: null, features: {} });
        return;
      }
      const data = await res.json();
      const map = {};
      (data.features || []).forEach((f) => { map[f.key] = f.value; });
      setState({
        ready: true,
        planId: data.plan_id,                 // EFFECTIVE plan
        planLabel: data.plan_label,
        rawPlanId: data.raw_plan_id,
        rawPlanLabel: data.raw_plan_label,
        activationStatus: data.activation_status,
        activationLabel: data.activation_label,
        effectiveStatus: data.effective_status,
        effectiveStatusLabel: data.effective_status_label,
        trialStartedAt: data.trial_started_at,
        trialEndsAt: data.trial_ends_at,
        daysRemaining: data.days_remaining,
        trialExpired: data.trial_expired,
        features: map,
        raw: data.features || [],
      });
    } catch {
      setState({ ready: true, planId: null, planLabel: null, features: {} });
    }
  };

  useEffect(() => { load(); }, []);

  const isEnabled = (key) => {
    if (!state.ready) return true;            // don't flicker-hide while loading
    if (state.planId === null) return true;   // fail-open on errors
    const v = state.features[key];
    return v === true || v === 'limited';
  };

  const isLimited = (key) => state.features[key] === 'limited';

  return (
    <FeatureContext.Provider value={{ ...state, isEnabled, isLimited, refresh: load }}>
      {children}
    </FeatureContext.Provider>
  );
}

export function useFeatures() {
  return useContext(FeatureContext);
}

export function useFeatureGate(key) {
  const { isEnabled, isLimited } = useContext(FeatureContext);
  return { enabled: isEnabled(key), limited: isLimited(key) };
}
